import React, {FunctionComponent, useState} from "react";

// @ts-ignore
import {makeStyles} from "tss-react/mui";

import {
    AppBar,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Toolbar,
    Tooltip,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {useHistory} from "react-router-dom";
import {useTranslation} from "@/Utils/CustomHooks/useTranslation";
import PreviousContractNotCheckedInModal
    from "@/Domains/DispositionManagement/Components/Modals/PreviousContractNotCheckedInModal";
import {Environment, QueryRenderer} from "react-relay";
import {relayAuthEnvironment} from "@/RelayEnvironment";
import {graphql} from "relay-runtime";
import {
    NewBookingPopupQuery
} from "@/Domains/DispositionManagement/Components/Modals/__generated__/NewBookingPopupQuery.graphql";
import {useSelector} from "react-redux";
import {selectViewerRights, ViewerState} from "@/Redux/Auth/AuthReducer";

interface INewBookingPopupProps {
    handleClose: () => void;
    predefinedStartdate?: string;
    predefinedVehicleId?: string;
    predefinedStartstationId?: string;
    predefinedVehicleGroupId?: string;
}

const useStyles = makeStyles()(() => ({
    appBar: {
        position: "relative",
        boxShadow: "none",
    },

    title: {
        flex: 1,
    },
}));

const NewBookingPopup: FunctionComponent<INewBookingPopupProps> = ({
                                                                       handleClose,
                                                                       predefinedStartdate,
                                                                       predefinedVehicleId,
                                                                       predefinedStartstationId,
                                                                       predefinedVehicleGroupId
                                                                   }): JSX.Element => {
    const {classes} = useStyles();
    const {t} = useTranslation();
    const history = useHistory();

    const [initialRun, setInitialRun] = useState(true);
    const [lastBooking, setLastBooking] = useState(null);
    const [bookingCIModalIsOpen, setBookingCIModalIsOpen] = useState(false);

    const rights = useSelector((state) =>
        selectViewerRights(state as ViewerState),
    );
    const vtcEnabled = rights.find(
        (right: any) => right.right === "VORG_VTC",
    );

    const runQuery = () => {
        if (!initialRun) {
            return renderContent();
        }

        return (
            <QueryRenderer<NewBookingPopupQuery>
                //@ts-ignore
                environment={relayAuthEnvironment() as Environment}
                query={graphql`
                    query NewBookingPopupQuery($vehicleId: String!, $date: String) {
                        lastBookingStatus(vehicleId: $vehicleId, date: $date) {
                            bookingStatus
                            lastBooking
                        }
                    }
                `}
                cacheConfig={{force: true}}
                variables={{
                    // @ts-ignore
                    vehicleId: predefinedVehicleId,
                    date: predefinedStartdate
                }}
                render={(result) => {
                    if (!result.props) {
                        return (
                            <div style={{textAlign: "center", width: "100%"}}>
                                <CircularProgress/>
                            </div>
                        );
                    }

                    setLastBooking({
                        lastBooking: result.props?.lastBookingStatus?.lastBooking,
                        bookingStatus: result.props?.lastBookingStatus?.bookingStatus
                    });

                    setInitialRun(false);

                    return renderContent();
                }}
            />
        );
    };

    const renderContent = () => {
        return (
            <>
                <Tooltip
                    title={t(
                        "disposition.actions.createNewReservationButtonLabel"
                    )}
                >
                    <Button
                        variant={"outlined"}
                        size={"small"}
                        startIcon={<AddIcon/>}
                        color={"secondary"}
                        onClick={() => {
                            history.push(
                                "/manager/reservation/new/show?predefinedStartdate=" +
                                predefinedStartdate +
                                "&predefinedVehicleId=" +
                                predefinedVehicleId +
                                "&predefinedStartstationId=" +
                                predefinedStartstationId +
                                "&predefinedVehicleGroupId=" +
                                predefinedVehicleGroupId
                            );
                        }}
                    >
                        {t(
                            "disposition.actions.createNewReservationButtonLabel"
                        )}
                    </Button>
                </Tooltip>{" "}
                <Tooltip
                    title={t(
                        "disposition.actions.createNewContractButtonLabel"
                    )}
                >
                    <Button
                        variant={"outlined"}
                        size={"small"}
                        startIcon={<AddIcon/>}
                        color={"secondary"}
                        onClick={() => {
                            if (lastBooking?.lastBooking && lastBooking?.bookingStatus != "ci_made") {
                                setBookingCIModalIsOpen(true);
                                return;
                            }

                            history.push(
                                "/manager/contract/new/show?predefinedStartdate=" +
                                predefinedStartdate +
                                "&predefinedVehicleId=" +
                                predefinedVehicleId +
                                "&predefinedStartstationId=" +
                                predefinedStartstationId +
                                "&predefinedVehicleGroupId=" +
                                predefinedVehicleGroupId
                            );
                        }}
                    >
                        {t("disposition.actions.createNewContractButtonLabel")}
                    </Button>
                </Tooltip>{" "}
                {vtcEnabled && <><Tooltip
                    title={t(
                        "disposition.actions.createNewTransferButtonLabel"
                    )}
                >
                    <Button
                        variant={"outlined"}
                        size={"small"}
                        startIcon={<AddIcon/>}
                        color={"secondary"}
                        onClick={() => {
                            if (lastBooking?.lastBooking && lastBooking?.bookingStatus != "ci_made") {
                                setBookingCIModalIsOpen(true);
                                return;
                            }

                            history.push(
                                "/manager/transfer/new/show?predefinedStartdate=" +
                                predefinedStartdate +
                                "&predefinedVehicleId=" +
                                predefinedVehicleId +
                                "&predefinedStartstationId=" +
                                predefinedStartstationId +
                                "&predefinedVehicleGroupId=" +
                                predefinedVehicleGroupId
                            );
                        }}
                    >
                        {t("disposition.actions.createNewTransferButtonLabel")}
                    </Button>
                </Tooltip>{" "}
                    <Tooltip
                        title={t(
                            "disposition.actions.createNewTransferReservationButtonLabel"
                        )}
                    >
                        <Button
                            variant={"outlined"}
                            size={"small"}
                            startIcon={<AddIcon/>}
                            color={"secondary"}
                            onClick={() => {
                                history.push(
                                    "/manager/transfer-reservation/new/show?predefinedStartdate=" +
                                    predefinedStartdate +
                                    "&predefinedVehicleId=" +
                                    predefinedVehicleId +
                                    "&predefinedStartstationId=" +
                                    predefinedStartstationId +
                                    "&predefinedVehicleGroupId=" +
                                    predefinedVehicleGroupId
                                );
                            }}
                        >
                            {t(
                                "disposition.actions.createNewTransferReservationButtonLabel"
                            )}
                        </Button>
                    </Tooltip>
                </>}
            </>
        )
    }

    const renderBookingCIModal = () => {
        if (!bookingCIModalIsOpen) {
            return <></>;
        }

        return <PreviousContractNotCheckedInModal open={bookingCIModalIsOpen}
                                                  handleClose={() => {
                                                      setBookingCIModalIsOpen(false)
                                                  }}
                                                  bookingId={lastBooking?.lastBooking}
                                                  type={lastBooking?.bookingStatus}
        />
    }

    return (
        <>
            {renderBookingCIModal()}
            <Dialog onClose={handleClose} maxWidth={"xl"} open={true}>
                <AppBar className={classes.appBar}>
                    <Toolbar>
                        <Typography variant="h6" className={classes.title}>
                            {t("disposition.messages.createNewVehicleBooking")}
                        </Typography>
                        <IconButton aria-label="close" onClick={handleClose}>
                            <CloseIcon/>
                        </IconButton>
                    </Toolbar>
                </AppBar>
                <DialogContent>
                    {runQuery()}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default React.memo(NewBookingPopup);
